/**
 * Custom element constants.
 */

export const TAG_NAME = 'fp-document-workspace';

export const ATTRIBUTES = {
  ASSET_BASE_URL: 'asset-base-url',
  CONTRACT_URL: 'contract-url',
  HIGHLIGHTED_BLOCK_ID: 'highlighted-block-id',
} as const;

/**
 * Event names are camelCase, not the kebab-case web-component convention.
 *
 * The consuming application is Angular 12.2, and its own React-embedding POC
 * demonstrates camelCase CustomEvents binding correctly in that template
 * compiler. Hyphenated names should work too, but this is not the place to bet
 * an integration on the less-travelled path in a compiler that old.
 *
 * `onError` rather than `error`: a bubbling, composed event literally named
 * `error` reaching `window` risks being picked up by the host's error
 * telemetry (the consumer runs the New Relic browser agent) and reported as a
 * script failure.
 */
export const EVENTS = {
  DOCUMENT_ERROR: 'onError',
  DOCUMENT_READY: 'onDocumentReady',
  TEXT_CLICKED: 'onTextClicked',
  TEXT_SELECTED: 'onTextSelected',
} as const;

/** Error codes carried on the `error` event's `detail.code`. */
export const ERROR_CODES = {
  CONTRACT_FETCH_FAILED: 'CONTRACT_FETCH_FAILED',
  CONTRACT_INVALID: 'CONTRACT_INVALID',
  NO_CONTRACT: 'NO_CONTRACT',
  /** A render-phase crash inside the document tree; see DocumentWorkspaceErrorBoundary. */
  RENDER_FAILURE: 'RENDER_FAILURE',
  /** A single block failed schema validation or its renderer threw; the rest of the document still rendered. */
  BLOCK_RENDER_FAILURE: 'BLOCK_RENDER_FAILURE',
} as const;

/**
 * Elements treated as a clickable unit. A click resolves to the nearest of
 * these ancestors, so clicking a word reports the whole paragraph it sits in.
 */
export const CLICK_TARGET_SELECTOR =
  '[data-block-key],p,h1,h2,h3,h4,h5,h6,li,td,th,blockquote,pre,figcaption,img';

/** Attributes the renderer's BlockAttributes extension preserves in the DOM. */
export const BLOCK_DATA_ATTRIBUTES = {
  KEY: 'data-block-key',
  ROLE: 'data-role',
  TYPE: 'data-block-type',
} as const;

/** Bounds the nearest-block search so it cannot escape the document area. */
export const VIEWER_CONTAINER_SELECTOR = '.fp-document-viewer-container';

export const OBSERVED_ATTRIBUTES: readonly string[] = Object.values(ATTRIBUTES);

/**
 * Stop-gap Hotjar targeting for the beta submission experience.
 *
 * Hotjar can only scope a survey popup by URL, and the popup must appear
 * everywhere in the beta flow EXCEPT the File Upload page and the
 * journal-selection Create page -- and never on the live experience. The
 * host page's own navigation is out of scope to change, so the element
 * manages this query param on the URL itself; see DocumentWorkspaceElement's
 * `_syncBetaExperienceUrlParam`.
 */
export const HOTJAR_BETA_QUERY_PARAM = 'fp';
export const HOTJAR_BETA_QUERY_VALUE = 'beta';

/**
 * DSW page path segments where the beta-experience query param must NOT
 * appear. Confirmed empirically:
 *  - File Upload lives at `/submission/fileUpload`, with no `step` param at
 *    all.
 *  - The journal-selection page a submission starts from lives at
 *    `/submission/create`, before File Upload and outside the numbered step
 *    flow entirely.
 * Every other page of the flow lives under `/submission/flow?...&step=N` and
 * gets marked as beta -- Manuscript, Subject, Authors, and beyond -- so path
 * is the only signal available; there is no step value to compare against
 * for either excluded page. If DSW ever renames one of these paths, this
 * silently stops excluding it and needs updating to match.
 */
export const BETA_EXPERIENCE_EXCLUDED_PATH_SEGMENTS = [
  '/fileUpload',
  '/submission/create',
] as const;
